(() => {
  if (customElements.get('kayoz-confidence-carousel')) return;

  class KayozConfidenceCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.kayoz-home-confidence__inner');
      this.slides = Array.from(this.querySelectorAll('[data-confidence-slide]'));

      if (!this.track || this.slides.length < 2) return;

      this.mobileQuery = window.matchMedia('(max-width: 719px)');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.intervalDelay = Math.max(4000, Number(this.dataset.interval) || 5000);
      this.currentIndex = 0;
      this.userPaused = false;
      this.scrollFrame = null;

      this.onPointerDown = () => this.pause(true);
      this.onMouseEnter = () => this.pause(true);
      this.onFocusIn = () => this.pause(true);
      this.onKeyDown = (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') this.pause(true);
      };
      this.onScroll = () => {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = window.requestAnimationFrame(() => this.updateCurrent());
      };
      this.onMediaChange = () => this.syncMode();
      this.onVisibilityChange = () => {
        if (document.hidden) this.stopTimer();
        else if (!this.userPaused) this.startTimer();
      };

      this.track.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.track.addEventListener('mouseenter', this.onMouseEnter, { passive: true });
      this.track.addEventListener('focusin', this.onFocusIn);
      this.track.addEventListener('keydown', this.onKeyDown);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.mobileQuery.addEventListener('change', this.onMediaChange);
      this.motionQuery.addEventListener('change', this.onMediaChange);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.syncMode();
    }

    disconnectedCallback() {
      this.stopTimer();
      window.cancelAnimationFrame(this.scrollFrame);
      this.track?.removeEventListener('pointerdown', this.onPointerDown);
      this.track?.removeEventListener('mouseenter', this.onMouseEnter);
      this.track?.removeEventListener('focusin', this.onFocusIn);
      this.track?.removeEventListener('keydown', this.onKeyDown);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.mobileQuery?.removeEventListener('change', this.onMediaChange);
      this.motionQuery?.removeEventListener('change', this.onMediaChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    syncMode() {
      const isMobile = this.mobileQuery.matches;
      const reduceMotion = this.motionQuery.matches;

      if (isMobile) {
        this.track.tabIndex = 0;
        this.track.setAttribute('aria-roledescription', 'carousel');
      } else {
        this.track.removeAttribute('tabindex');
        this.track.removeAttribute('aria-roledescription');
        this.track.scrollLeft = 0;
      }

      if (!isMobile || reduceMotion || !this.autoplayEnabled || this.userPaused) {
        this.stopTimer();
      } else {
        this.startTimer();
      }

      this.updateCurrent();
    }

    startTimer() {
      if (!this.mobileQuery.matches || this.motionQuery.matches || !this.autoplayEnabled || document.hidden) return;
      this.stopTimer();
      this.timer = window.setInterval(() => this.advance(), this.intervalDelay);
    }

    stopTimer() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    pause(fromUser = false) {
      if (fromUser) this.userPaused = true;
      this.stopTimer();
    }

    advance() {
      this.currentIndex = (this.currentIndex + 1) % this.slides.length;
      this.track.scrollTo({ left: this.currentIndex * this.track.clientWidth, behavior: 'smooth' });
      this.setCurrent(this.currentIndex);
    }

    updateCurrent() {
      if (!this.track.clientWidth) return;
      const index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / this.track.clientWidth)));
      this.currentIndex = index;
      this.setCurrent(index);
    }

    setCurrent(index) {
      this.slides.forEach((slide, slideIndex) => {
        if (slideIndex === index) slide.setAttribute('aria-current', 'true');
        else slide.removeAttribute('aria-current');
      });
    }
  }

  customElements.define('kayoz-confidence-carousel', KayozConfidenceCarousel);
})();
